import React from 'react';
import { RankingSnapshot } from '../services/api';
import { getActiveHoursLabel } from '../config/activeHours';

interface DpdTrendProps {
  ranking: RankingSnapshot;
  compact?: boolean;
}

const DpdTrend: React.FC<DpdTrendProps> = ({ ranking, compact = true }) => {
  const dpd0 = ranking.dpd_0 || 0;
  const dpd1 = ranking.dpd_1 || 0;
  const dpd3 = ranking.dpd_3 || 0;
  const dpd7 = ranking.dpd_7 || 0;

  // Calcular tendência
  const getTrend = () => {
    if (dpd0 > dpd1 && dpd1 >= dpd3) return 'up';
    if (dpd0 < dpd1 && dpd1 <= dpd3) return 'down';
    if (dpd0 === 0 && dpd1 === 0 && dpd3 === 0) return 'dead';
    if (dpd0 > 0 && dpd1 === 0) return 'recovery';
    return 'stable';
  };

  const trend = getTrend();

  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return <i className="bi bi-arrow-up-circle-fill text-success" title="Vendas em alta"></i>;
      case 'down':
        return <i className="bi bi-arrow-down-circle-fill text-danger" title="Vendas em queda"></i>;
      case 'dead':
        return <i className="bi bi-x-circle-fill text-dark" title="Sem vendas"></i>;
      case 'recovery':
        return <i className="bi bi-arrow-return-right text-info" title="Em recuperação"></i>;
      default:
        return <i className="bi bi-dash-circle text-muted" title="Vendas estáveis"></i>;
    }
  };

  const formatNumber = (value: number): string => {
    return value.toFixed(1);
  };

  // Calcular valores para o mini gráfico
  const maxValue = Math.max(dpd0, dpd1, dpd3, 1);
  const points = [
    { day: 'D2-4', value: dpd3, height: (dpd3 / maxValue) * 100 },
    { day: 'Ontem', value: dpd1, height: (dpd1 / maxValue) * 100 },
    { day: 'Hoje', value: dpd0, height: (dpd0 / maxValue) * 100 }
  ];

  // Helper para determinar badge de velocidade V2.0
  const getVelocityBadge = () => {
    const category = ranking.penalty_v2_category;
    if (!category || category === 'v1_mode') return null;

    const categoryColors: { [key: string]: string } = {
      'ultra-fast': 'danger',
      'fast': 'warning',
      'medium': 'info',
      'slow': 'success',
      'new_product': 'secondary'
    };

    const categoryLabels: { [key: string]: string } = {
      'ultra-fast': 'Ultra',
      'fast': 'Fast',
      'medium': 'Med',
      'slow': 'Slow',
      'new_product': 'Novo'
    };

    const color = categoryColors[category] || 'secondary';
    const label = categoryLabels[category] || category;

    return (
      <span
        className={`badge bg-${color} ms-1`}
        style={{ fontSize: '0.65em' }}
        title={`Velocidade V2.0: ${category} (DPD 7: ${formatNumber(dpd7)})`}
      >
        {label}
      </span>
    );
  };

  if (compact) {
    // Versão compacta para a tabela
    return (
      <div className="dpd-trend-compact">
        <div className="d-flex align-items-center">
          <div className="me-2">
            {getTrendIcon()}
          </div>
          <div className="small">
            <div>H: <strong>{formatNumber(dpd0)}</strong></div>
            <div className="text-muted">O: {formatNumber(dpd1)}</div>
            <div className="text-muted" style={{ fontSize: '0.8em' }}>
              3d: {formatNumber(dpd3)} | 7d: {formatNumber(dpd7)}
              {getVelocityBadge()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Versão completa com gráfico
  return (
    <div className="dpd-trend-full">
      <h6 className="mb-3">Tendência de Vendas (DPD)</h6>
      
      {/* Mini gráfico de barras */}
      <div className="d-flex align-items-end justify-content-around mb-3" style={{ height: '100px' }}>
        {points.map((point, index) => (
          <div key={index} className="text-center" style={{ flex: 1 }}>
            <div
              className={`bg-${trend === 'up' && index === 2 ? 'success' : 
                         trend === 'down' && index === 2 ? 'danger' : 
                         'secondary'} mx-1`}
              style={{
                height: `${point.height}%`,
                minHeight: '2px',
                transition: 'height 0.3s ease'
              }}
              title={`${point.day}: ${point.value}`}
            />
            <div className="small mt-1">{formatNumber(point.value)}</div>
            <div className="small text-muted">{point.day}</div>
          </div>
        ))}
      </div>

      {/* Análise da tendência */}
      <div className="alert alert-sm p-2" role="alert" 
           style={{ 
             backgroundColor: trend === 'up' ? '#d4edda' : 
                            trend === 'down' ? '#f8d7da' : 
                            trend === 'dead' ? '#d6d8db' :
                            trend === 'recovery' ? '#d1ecf1' : '#fff3cd',
             borderColor: trend === 'up' ? '#c3e6cb' : 
                         trend === 'down' ? '#f5c6cb' : 
                         trend === 'dead' ? '#c6c8ca' :
                         trend === 'recovery' ? '#bee5eb' : '#ffeeba'
           }}>
        <div className="d-flex align-items-center">
          {getTrendIcon()}
          <span className="ms-2 small">
            {trend === 'up' && 'Vendas em crescimento'}
            {trend === 'down' && 'Vendas em declínio'}
            {trend === 'dead' && 'Produto sem vendas recentes'}
            {trend === 'recovery' && 'Produto em recuperação'}
            {trend === 'stable' && 'Vendas estáveis'}
          </span>
        </div>
      </div>

      {/* Detalhes */}
      <div className="small">
        <div className="d-flex justify-content-between mb-1">
          <span className="text-muted">Vendas Hoje (DPD_0):</span>
          <strong>{dpd0}</strong>
        </div>
        <div className="d-flex justify-content-between mb-1">
          <span className="text-muted">Vendas Ontem (DPD_1):</span>
          <span>{dpd1}</span>
        </div>
        <div className="d-flex justify-content-between mb-1">
          <span className="text-muted">Média D2-4 (DPD_3):</span>
          <span>{formatNumber(dpd3)}</span>
        </div>
        <div className="d-flex justify-content-between mb-1">
          <span className="text-muted">Média 7 dias (DPD_7):</span>
          <strong>{formatNumber(dpd7)}</strong>
        </div>
        {ranking.penalty_v2_category && ranking.penalty_v2_category !== 'v1_mode' && (
          <div className="mt-2 p-2 bg-light rounded">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="text-muted small">Classificação V2.0:</span>
              {getVelocityBadge()}
            </div>
            <div className="small text-muted">
              Base para penalização temporal ({getActiveHoursLabel()})
            </div>
          </div>
        )}
        <hr />
        <div className="d-flex justify-content-between">
          <span className="text-muted">Vendas 6 dias:</span>
          <span>{ranking.vendas_6_dias || 0}</span>
        </div>
        <div className="d-flex justify-content-between">
          <span className="text-muted">Vendas históricas:</span>
          <span>{ranking.vendas_historicas || 0}</span>
        </div>
      </div>
    </div>
  );
};

export default DpdTrend;